import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  BadgeCheck,
  Boxes,
  ChevronDown,
  ChevronUp,
  Eye,
  Layers3,
  Tag,
  Type,
} from 'lucide-react';
import { focarObjeto } from '../../../../lib/corelOperations';
import { coletarArte, escanearPagina, limparArte, marcarArte, validarCasamentoArte } from '../services';
import type {
  ApplyArtRequest,
  ApplyArtValidationResult,
  ArtSelectionItem,
  ArtSelectionResponse,
  EscanearPaginaResponse,
  TextPlaceholderId,
} from '../types';
import { ArtConfigSheet, type ArtSlotOption } from './ArtConfigSheet';

type ApplyArtePlaceholderProps = { onBack?: () => void };
type TextDraft = Record<TextPlaceholderId, string>;

const EMPTY_TEXTS: TextDraft = { nome: '', sobrenome: '', numero: '', tamanho: '' };
const TEXT_TOKEN_TO_FIELD: Record<string, TextPlaceholderId> = {
  $: 'nome',
  '%': 'sobrenome',
  '#': 'numero',
  '&': 'tamanho',
};
const EMPTY_SCAN: EscanearPaginaResponse = {
  pagina: { nome: '', indice: 0, documentoNome: '', documentoCaminho: '', salvoNoDisco: false },
  resumo: { totalCandidatos: 0, mapeados: 0, pendentes: 0, placeholders: 0, conflitos: 0 },
  molde: { familiaTamanho: '', tamanho: '', custom: false, paginaCanonica: '', sincronizadoComPagina: false },
  checklist: [],
  candidatos: [],
  warnings: [],
};
const EMPTY_ART: ArtSelectionResponse = {
  documento: { nome: '', caminho: '', salvoNoDisco: false },
  pagina: { nome: '', indice: 0 },
  resumo: { total: 0, marcados: 0, pendentes: 0, conflitos: 0 },
  molde: { familiaTamanho: '', tamanho: '', custom: false, paginaCanonica: '', sincronizadoComPagina: false },
  objetos: [],
  warnings: [],
};

function createFocusPromise(staticId: number) {
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const requestId = focarObjeto(String(staticId), finish);
    if (!requestId) finish();
    window.setTimeout(finish, 450);
  });
}

function getStateMeta(state: ArtSelectionItem['state']) {
  if (state === 'marcado') {
    return {
      label: 'Marcado',
      className: 'border-green-700/30 bg-green-900/10 text-green-200',
    };
  }

  if (state === 'conflito') {
    return {
      label: 'Conflito',
      className: 'border-red-700/30 bg-red-900/10 text-red-200',
    };
  }

  return {
    label: 'Pendente',
    className: 'border-yellow-700/30 bg-yellow-900/10 text-yellow-200',
  };
}

function isAutomaticTextItem(item: ArtSelectionItem) {
  return item.slotKind === 'texto' && (item.detectedTextTokens?.length ?? 0) > 0;
}

function getExpectedSlotOptions(scan: EscanearPaginaResponse): ArtSlotOption[] {
  const slots = new Map<string, ArtSlotOption>();
  scan.candidatos.forEach((item) => {
    if (!item.identificador || item.state !== 'mapeado') return;
    if (
      item.isText ||
      item.peca === 'texto' ||
      item.peca === 'emblema' ||
      item.peca === 'background' ||
      item.peca === 'vetor' ||
      (item.peca === 'extra' && item.parte === 'etiqueta')
    ) {
      return;
    }
    slots.set(item.identificador, { value: item.identificador, label: item.identificador, kind: 'estrutural' });
  });
  return Array.from(slots.values()).sort((a, b) => a.label.localeCompare(b.label));
}

function getMarkedTextSlots(selection: ArtSelectionResponse) {
  const placeholders = new Set<TextPlaceholderId>();
  selection.objetos.forEach((item) => {
    const slotKind = (item.slotKind || '').trim();
    const slotId = (item.slotId || '').trim();
    if (slotKind !== 'texto' && slotId !== 'texto') return;
    (item.detectedTextTokens || []).forEach((token) => {
      const field = TEXT_TOKEN_TO_FIELD[token];
      if (field) placeholders.add(field);
    });
  });
  return Array.from(placeholders.values()).sort((a, b) => a.localeCompare(b));
}

function buildMatchedSlots(result: ApplyArtValidationResult | null) {
  return new Set<string>([...(result?.matchedStructural || [])]);
}

function buildMissingSlots(result: ApplyArtValidationResult | null) {
  return new Set<string>([...(result?.missingStructural || [])]);
}

function getValidationMeta(result: ApplyArtValidationResult | null) {
  if (!result) {
    return {
      label: 'Aguardando',
      bannerClass: 'border-navy-700 bg-navy-950/60 text-navy-200',
      pillClass: 'border-navy-700 bg-navy-950/60 text-navy-200',
      message: 'Leia o molde, a selecao e valide quando estiver pronto.',
    };
  }

  if (result.status === 'pronto') {
    return {
      label: 'Pronto',
      bannerClass: 'border-green-700/30 bg-green-900/10 text-green-200',
      pillClass: 'border-green-700/30 bg-green-900/10 text-green-200',
      message: 'Contrato pronto para a proxima fase.',
    };
  }

  if (result.status === 'com_pendencias') {
    return {
      label: 'Pendencias',
      bannerClass: 'border-yellow-700/30 bg-yellow-900/10 text-yellow-100',
      pillClass: 'border-yellow-700/30 bg-yellow-900/10 text-yellow-100',
      message: 'Revise faltas estruturais, valores ausentes e avisos dos textos da arte antes de seguir.',
    };
  }

  return {
    label: 'Invalido',
    bannerClass: 'border-red-700/30 bg-red-900/10 text-red-200',
    pillClass: 'border-red-700/30 bg-red-900/10 text-red-200',
    message: 'Existe conflito ou slot invalido na selecao.',
  };
}

function PreviewCard({ item }: { item: ArtSelectionItem }) {
  const aspectRatio = item.previewBox?.aspectRatio && item.previewBox.aspectRatio > 0 ? item.previewBox.aspectRatio : 1;
  const width = aspectRatio >= 1 ? '78%' : `${Math.max(24, Math.min(78, 78 * aspectRatio))}%`;
  const height = aspectRatio >= 1 ? `${Math.max(24, Math.min(78, 78 / aspectRatio))}%` : '78%';
  return (
    <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-navy-700 bg-navy-950/70 p-2">
      <div className="relative h-full w-full overflow-hidden rounded-md bg-navy-900/40">
        <div
          className="absolute left-1/2 top-1/2 rounded-sm border border-cyan-400/80 bg-cyan-400/5"
          style={{ width, height, transform: 'translate(-50%, -50%)' }}
        />
      </div>
    </div>
  );
}

export function ApplyArtePlaceholder({ onBack }: ApplyArtePlaceholderProps) {
  const [scan, setScan] = useState<EscanearPaginaResponse>(EMPTY_SCAN);
  const [selection, setSelection] = useState<ArtSelectionResponse>(EMPTY_ART);
  const [texts, setTexts] = useState<TextDraft>(EMPTY_TEXTS);
  const [result, setResult] = useState<ApplyArtValidationResult | null>(null);
  const [message, setMessage] = useState<{ type: 'ok' | 'erro'; text: string } | null>(null);
  const [loadingMolde, setLoadingMolde] = useState(false);
  const [loadingSelection, setLoadingSelection] = useState(false);
  const [validating, setValidating] = useState(false);
  const [mutatingItemId, setMutatingItemId] = useState<number | null>(null);
  const [sheetItemId, setSheetItemId] = useState<number | null>(null);
  const [showDataBlock, setShowDataBlock] = useState(false);

  useEffect(() => {
    if (!message) return undefined;
    const timeoutId = window.setTimeout(() => setMessage(null), 3500);
    return () => window.clearTimeout(timeoutId);
  }, [message]);

  const slotOptions = useMemo(() => getExpectedSlotOptions(scan), [scan]);
  const textSlots = useMemo(() => getMarkedTextSlots(selection), [selection]);
  const matchedSlots = useMemo(() => buildMatchedSlots(result), [result]);
  const missingSlots = useMemo(() => buildMissingSlots(result), [result]);
  const validationMeta = useMemo(() => getValidationMeta(result), [result]);
  const sheetItem = useMemo(() => selection.objetos.find((item) => item.staticId === sheetItemId) || null, [selection.objetos, sheetItemId]);

  const showMessage = useCallback((text: string, type: 'ok' | 'erro') => setMessage({ text, type }), []);

  const loadMolde = useCallback(async () => {
    setLoadingMolde(true);
    try {
      const response = await escanearPagina();
      setScan(response);
      setTexts((current) => ({ ...current, tamanho: current.tamanho || response.molde?.tamanho || '' }));
    } catch (error: any) {
      showMessage(error?.message || 'Falha ao ler o molde destino.', 'erro');
    } finally {
      setLoadingMolde(false);
    }
  }, [showMessage]);

  const loadSelection = useCallback(async () => {
    setLoadingSelection(true);
    try {
      setSelection(await coletarArte());
    } catch (error: any) {
      showMessage(error?.message || 'Falha ao ler a selecao atual.', 'erro');
    } finally {
      setLoadingSelection(false);
    }
  }, [showMessage]);

  useEffect(() => {
    void loadMolde();
  }, [loadMolde]);

  useEffect(() => {
    if (textSlots.length > 0) {
      setShowDataBlock(true);
    }
  }, [textSlots.length]);

  const handleFocus = useCallback(async (item: ArtSelectionItem) => {
    await createFocusPromise(item.staticId);
    showMessage(`Objeto ${item.shapeName || item.staticId} focado no Corel.`, 'ok');
  }, [showMessage]);

  const handleApplySlot = useCallback(async (slotId: string) => {
    if (!sheetItem) return;
    setMutatingItemId(sheetItem.staticId);
    try {
      await marcarArte({ staticId: sheetItem.staticId, slotId });
      await loadSelection();
      setResult(null);
      setSheetItemId(null);
      showMessage(`Arte marcada como ${slotId}.`, 'ok');
    } catch (error: any) {
      showMessage(error?.message || 'Falha ao marcar a arte.', 'erro');
    } finally {
      setMutatingItemId(null);
    }
  }, [loadSelection, sheetItem, showMessage]);

  const handleClearSlot = useCallback(async () => {
    if (!sheetItem) return;
    setMutatingItemId(sheetItem.staticId);
    try {
      await limparArte({ staticId: sheetItem.staticId });
      await loadSelection();
      setResult(null);
      setSheetItemId(null);
      showMessage('Marcacao da arte removida.', 'ok');
    } catch (error: any) {
      showMessage(error?.message || 'Falha ao limpar a arte.', 'erro');
    } finally {
      setMutatingItemId(null);
    }
  }, [loadSelection, sheetItem, showMessage]);

  const handleValidate = useCallback(async () => {
    if (!scan.pagina.nome) {
      showMessage('Leia primeiro o molde destino.', 'erro');
      return;
    }
    setValidating(true);
    try {
      const textos = Object.fromEntries(Object.entries(texts).map(([key, value]) => [key, value.trim()]).filter((entry) => Boolean(entry[1]))) as ApplyArtRequest['textos'];
      setResult(await validarCasamentoArte({
        fonte: 'selecao_atual',
        moldeRef: { arquivo: scan.pagina.documentoCaminho || '', pagina: scan.pagina.nome },
        textos,
      }));
    } catch (error: any) {
      showMessage(error?.message || 'Falha ao validar o casamento arte > molde.', 'erro');
    } finally {
      setValidating(false);
    }
  }, [scan.pagina.documentoCaminho, scan.pagina.nome, showMessage, texts]);

  const pendingCount = result
    ? result.missingStructural.length + result.missingTexts.length + result.textWarnings.length
    : selection.resumo.pendentes + selection.resumo.conflitos;
  const conflictCount = result ? result.invalidSlots.length + result.errors.length : selection.resumo.conflitos;
  const markedCount = result
    ? result.matchedStructural.length + result.matchedEmblems.length + result.matchedTexts.length
    : selection.resumo.marcados;
  const validationIssuesCount = result
    ? result.missingStructural.length + result.missingTexts.length + result.textWarnings.length + result.invalidSlots.length + result.errors.length
    : 0;
  const validationNotesCount = result ? validationIssuesCount + result.unusedInputs.length : 0;
  const currentPath = scan.pagina.documentoCaminho || selection.documento.caminho || '';
  const currentDocumentName = scan.pagina.documentoNome || selection.documento.nome || 'Documento atual';

  return (
    <div className="relative h-full overflow-hidden bg-navy-950 text-white">
      <div className="flex h-full flex-col">
        <div className="border-b border-navy-700 bg-navy-950/90 px-3 py-2.5">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {onBack && (
                  <button
                    onClick={onBack}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-navy-700 bg-navy-900 text-navy-200 transition-colors hover:bg-navy-800 hover:text-white"
                    title="Voltar ao hub"
                  >
                    <ArrowLeft size={14} />
                  </button>
                )}
                <h2 className="text-sm font-semibold text-yellow-400">Aplicar Arte</h2>
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
              <button
                onClick={() => void loadMolde()}
                disabled={loadingMolde || validating}
                className="rounded-lg border border-navy-600 bg-navy-800 px-2.5 py-1.5 text-[10px] font-semibold text-navy-100 transition-colors hover:bg-navy-700 disabled:opacity-50"
              >
                {loadingMolde ? 'Lendo...' : 'Ler molde'}
              </button>
              <button
                onClick={() => void loadSelection()}
                disabled={loadingSelection || validating}
                className="rounded-lg border border-navy-600 bg-navy-800 px-2.5 py-1.5 text-[10px] font-semibold text-navy-100 transition-colors hover:bg-navy-700 disabled:opacity-50"
              >
                {loadingSelection ? 'Lendo...' : 'Ler selecao'}
              </button>
              <button
                onClick={() => void handleValidate()}
                disabled={loadingMolde || loadingSelection || validating || !scan.pagina.nome}
                className="inline-flex items-center gap-1.5 rounded-lg bg-yellow-500 px-2.5 py-1.5 text-[10px] font-semibold text-navy-950 transition-colors hover:bg-yellow-400 disabled:opacity-50"
              >
                <BadgeCheck size={12} />
                {validating ? 'Validando...' : 'Validar'}
              </button>
              <button
                disabled
                className="inline-flex items-center gap-1.5 rounded-lg border border-navy-700 bg-navy-950/50 px-2.5 py-1.5 text-[10px] font-semibold text-navy-400 opacity-70"
              >
                <Tag size={12} />
                Aplicar
              </button>
            </div>
          </div>

          {message && (
            <div
              className={`mt-2 rounded-lg border px-2.5 py-2 text-[11px] ${
                message.type === 'ok'
                  ? 'border-green-700/30 bg-green-900/10 text-green-200'
                  : 'border-red-700/30 bg-red-900/10 text-red-200'
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-5">
            <div className="rounded-xl border border-navy-700 bg-navy-900/70 px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-[0.16em] text-navy-400">Pagina</div>
              <div className="mt-0.5 text-sm font-semibold text-white">{scan.pagina.nome || '--'}</div>
            </div>
            <div className="rounded-xl border border-cyan-700/30 bg-cyan-900/10 px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-[0.16em] text-cyan-300">Tamanho</div>
              <div className="mt-0.5 text-sm font-semibold text-white">{scan.molde?.tamanho || 'pendente'}</div>
            </div>
            <div className="rounded-xl border border-green-700/30 bg-green-900/10 px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-[0.16em] text-green-300">Marcados</div>
              <div className="mt-0.5 text-sm font-semibold text-white">{String(markedCount).padStart(2, '0')}</div>
            </div>
            <div className="rounded-xl border border-yellow-700/30 bg-yellow-900/10 px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-[0.16em] text-yellow-300">Pendencias</div>
              <div className="mt-0.5 text-sm font-semibold text-white">{String(pendingCount).padStart(2, '0')}</div>
            </div>
            <div className="rounded-xl border border-red-700/30 bg-red-900/10 px-2.5 py-2">
              <div className="text-[9px] uppercase tracking-[0.16em] text-red-300">Conflitos</div>
              <div className="mt-0.5 text-sm font-semibold text-white">{String(conflictCount).padStart(2, '0')}</div>
            </div>
          </div>

          <div className="mt-2 rounded-xl border border-navy-700 bg-navy-900/50 px-2.5 py-2 text-[11px] text-navy-300">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-navy-100">{currentDocumentName}</div>
                <div className="truncate text-[10px] text-navy-400">
                  {currentPath || 'Sem caminho detectado'}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="rounded-full border border-cyan-700/30 bg-cyan-900/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-cyan-200">
                  Estrutural {slotOptions.length}
                </span>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                    scan.molde?.tamanho
                      ? 'border-cyan-700/30 bg-cyan-900/10 text-cyan-200'
                      : 'border-yellow-700/30 bg-yellow-900/10 text-yellow-200'
                  }`}
                >
                  Tamanho {scan.molde?.tamanho || 'pendente'}
                </span>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                    scan.molde?.sincronizadoComPagina
                      ? 'border-green-700/30 bg-green-900/10 text-green-200'
                      : 'border-yellow-700/30 bg-yellow-900/10 text-yellow-200'
                  }`}
                >
                  {scan.molde?.sincronizadoComPagina ? `Pagina ${scan.pagina.nome || '--'}` : 'Pagina diferente'}
                </span>
                <span
                  className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${validationMeta.pillClass}`}
                >
                  {validationMeta.label}
                </span>
              </div>
            </div>

            {!scan.molde?.sincronizadoComPagina && scan.pagina.nome && (
              <div className="mt-2 rounded-lg border border-yellow-700/30 bg-yellow-900/10 px-2.5 py-2 text-[10px] text-yellow-200">
                Finalize o molde antes de aplicar.
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          <div className="rounded-xl border border-navy-700 bg-navy-900/50 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Layers3 size={14} className="text-yellow-400" />
                <h3 className="text-[13px] font-semibold text-yellow-400">Objetos</h3>
                <span className="rounded-full border border-navy-600 bg-navy-800 px-2 py-0.5 text-[9px] font-semibold text-navy-300">
                  {selection.objetos.length}
                </span>
              </div>
              <button onClick={() => void loadSelection()} disabled={loadingSelection} className="rounded-lg border border-navy-600 bg-navy-800 px-2.5 py-1.5 text-[10px] font-semibold text-navy-100 transition-colors hover:bg-navy-700 disabled:opacity-60">
                {loadingSelection ? 'Lendo...' : 'Ler selecao'}
              </button>
            </div>

            {slotOptions.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {slotOptions.map((slot) => {
                  const className = matchedSlots.has(slot.value)
                    ? 'border-green-700/40 bg-green-900/15 text-green-200'
                    : missingSlots.has(slot.value)
                      ? 'border-yellow-700/40 bg-yellow-900/15 text-yellow-100'
                      : 'border-navy-700 bg-navy-950/60 text-navy-200';

                  return (
                    <span key={slot.value} className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${className}`}>
                      {slot.label}
                    </span>
                  );
                })}
              </div>
            )}

            {selection.warnings.length > 0 && (
              <div className="mt-2 space-y-2">
                {selection.warnings.map((warning, index) => (
                  <div
                    key={`${warning}-${index}`}
                    className="rounded-lg border border-yellow-700/30 bg-yellow-900/10 px-2.5 py-2 text-[11px] text-yellow-200"
                  >
                    {warning}
                  </div>
                ))}
              </div>
            )}

            {selection.objetos.some(isAutomaticTextItem) && (
              <div className="mt-2 rounded-lg border border-cyan-700/30 bg-cyan-900/10 px-2.5 py-2 text-[11px] text-cyan-100">
                Textos com <span className="font-semibold">$ % # &amp;</span> entram automaticamente na arte. Nesses casos, voce so precisa preencher os valores em Dados variaveis.
              </div>
            )}

            <div className="mt-4 space-y-3">
              {selection.objetos.length === 0 && <div className="rounded-xl border border-dashed border-navy-700 bg-navy-950/40 px-3 py-8 text-center text-xs text-navy-400">Selecione objetos e clique em Ler selecao.</div>}
              {selection.objetos.map((item) => {
                const stateMeta = getStateMeta(item.state);
                const autoText = isAutomaticTextItem(item);

                return (
                  <div
                    key={item.staticId}
                    className={`rounded-xl border px-2.5 py-2.5 transition-colors ${
                      sheetItemId === item.staticId ? 'border-yellow-500 bg-navy-800/80' : 'border-navy-700 bg-navy-900/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <PreviewCard item={item} />
                      <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="truncate text-[13px] font-semibold leading-5 text-white">{item.shapeName || `Shape #${item.staticId}`}</div>
                            <span className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${stateMeta.className}`}>{stateMeta.label}</span>
                          </div>

                          <div className="mt-0.5 text-[10px] text-navy-400">
                            {item.layerName || 'Sem camada'} - #{item.staticId}
                          </div>

                          <div className="mt-1 text-[10px] font-medium text-yellow-300">
                            {autoText ? 'Texto automatico' : item.slotId || 'Sem destino definido'}
                          </div>

                          {item.slotKind === 'texto' && (item.detectedTextTokens?.length || item.textWarnings?.length) ? (
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {(item.detectedTextTokens || []).map((token) => (
                                <span
                                  key={`${item.staticId}-${token}`}
                                  className="rounded-full border border-cyan-700/40 bg-cyan-900/20 px-2 py-0.5 text-[9px] font-semibold text-cyan-200"
                                >
                                  {token}
                                </span>
                              ))}
                              {(item.textWarnings || []).map((warning, index) => (
                                <span
                                  key={`${item.staticId}-warning-${index}`}
                                  className="rounded-full border border-yellow-700/40 bg-yellow-900/20 px-2 py-0.5 text-[9px] font-semibold text-yellow-100"
                                >
                                  sem token
                                </span>
                              ))}
                            </div>
                          ) : null}

                          {item.problems?.length ? (
                            <div className="mt-1.5 space-y-1">
                              {item.problems.map((problem, index) => (
                                <div key={`${item.staticId}-${index}`} className="text-[10px] text-red-200">
                                  {problem}
                                </div>
                              ))}
                            </div>
                          ) : null}

                          {item.textWarnings?.length ? (
                            <div className="mt-1.5 space-y-1">
                              {item.textWarnings.map((warning, index) => (
                                <div key={`${item.staticId}-text-${index}`} className="text-[10px] text-yellow-200">
                                  {warning}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        <div className="flex shrink-0 gap-1.5">
                          <button
                            onClick={() => void handleFocus(item)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-navy-600 bg-navy-800 text-navy-100 transition-colors hover:bg-navy-700"
                            title="Focar no Corel"
                          >
                            <Eye size={15} />
                          </button>
                          {autoText ? (
                            <span
                              className="inline-flex h-9 items-center justify-center rounded-lg border border-cyan-700/40 bg-cyan-900/20 px-2.5 text-[10px] font-semibold uppercase tracking-wide text-cyan-100"
                              title="Texto reconhecido automaticamente"
                            >
                              Auto
                            </span>
                          ) : (
                            <button
                              onClick={() => setSheetItemId(item.staticId)}
                              disabled={Boolean(mutatingItemId)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-yellow-500 text-navy-950 transition-colors hover:bg-yellow-400 disabled:opacity-60"
                              title="Configurar"
                            >
                              <Tag size={15} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <section className="mt-3 rounded-xl border border-navy-700 bg-navy-900/50 p-3">
            <button onClick={() => setShowDataBlock((current) => !current)} className="flex w-full items-center justify-between gap-3 text-left">
              <div className="flex items-center gap-2">
                <Type size={14} className="text-cyan-300" />
                <h3 className="text-[13px] font-semibold text-cyan-200">Dados variaveis</h3>
                <span className="rounded-full border border-navy-600 bg-navy-800 px-2 py-0.5 text-[9px] font-semibold text-navy-300">
                  {textSlots.length}
                </span>
              </div>
              {showDataBlock ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {showDataBlock && (
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {([
                  ['nome', 'Nome'],
                  ['sobrenome', 'Sobrenome'],
                  ['numero', 'Numero'],
                  ['tamanho', 'Tamanho / Etiqueta'],
                ] as Array<[TextPlaceholderId, string]>).map(([key, label]) => (
                  <label key={key} className="block">
                    <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy-400">
                      <span>{label}</span>
                      {textSlots.includes(key) && <span className="rounded-full border border-cyan-700/40 bg-cyan-900/20 px-2 py-0.5 text-[10px] text-cyan-200">slot</span>}
                    </div>
                    <input value={texts[key]} onChange={(event) => setTexts((current) => ({ ...current, [key]: event.target.value }))} placeholder={`Valor para ${label.toLowerCase()}`} className="w-full rounded-xl border border-navy-600 bg-navy-950/70 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400" />
                  </label>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-navy-700 bg-navy-900/50 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Boxes size={14} className="text-cyan-300" />
                <h3 className="text-[13px] font-semibold text-cyan-200">Validacao</h3>
              </div>
              <span className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${validationMeta.pillClass}`}>
                {validationMeta.label}
              </span>
            </div>
            <div className={`mt-3 rounded-lg border px-3 py-2 text-[11px] ${validationMeta.bannerClass}`}>
              {validationMeta.message}
            </div>
            {result && (
              <div className="mt-4 space-y-3">
                <div className="grid gap-2 md:grid-cols-4 text-[11px]">
                  <div className="rounded-xl border border-navy-700 bg-navy-950/60 px-3 py-2">Estrutural {result.resumo.structuralMatched}/{result.resumo.structuralExpected}</div>
                  <div className="rounded-xl border border-navy-700 bg-navy-950/60 px-3 py-2">Emblemas {result.resumo.emblemsMatched}</div>
                  <div className="rounded-xl border border-navy-700 bg-navy-950/60 px-3 py-2">Textos {result.resumo.textMatched}/{result.resumo.textExpected}</div>
                  <div className="rounded-xl border border-navy-700 bg-navy-950/60 px-3 py-2">Sem destino {result.resumo.unusedInputs}</div>
                </div>
                {validationNotesCount > 0 && (
                  <div className="space-y-2 text-[11px]">
                    {result.missingStructural.length > 0 && (
                      <div className="rounded-lg border border-yellow-700/30 bg-yellow-900/10 px-3 py-2 text-yellow-100">
                        Estrutural pendente: {result.missingStructural.join(', ')}
                        <div className="mt-1 text-[10px] text-yellow-200/90">
                          O operador pode decidir seguir com pendencias na etapa de geracao.
                        </div>
                      </div>
                    )}
                    {result.missingTexts.length > 0 && <div className="rounded-lg border border-yellow-700/30 bg-yellow-900/10 px-3 py-2 text-yellow-100">Textos pendentes: {result.missingTexts.join(', ')}</div>}
                    {result.textWarnings.length > 0 && <div className="rounded-lg border border-yellow-700/30 bg-yellow-900/10 px-3 py-2 text-yellow-100">Avisos de texto: {result.textWarnings.join(' | ')}</div>}
                    {result.unusedInputs.length > 0 && <div className="rounded-lg border border-navy-700 bg-navy-950/60 px-3 py-2 text-navy-200">Sem destino: {result.unusedInputs.map((item) => item.label).join(', ')}</div>}
                    {result.invalidSlots.length > 0 && <div className="rounded-lg border border-red-700/30 bg-red-900/10 px-3 py-2 text-red-200">Conflitos: {result.invalidSlots.join(' | ')}</div>}
                    {result.errors.length > 0 && <div className="rounded-lg border border-red-700/30 bg-red-900/10 px-3 py-2 text-red-200">Erros: {result.errors.join(' | ')}</div>}
                  </div>
                )}
                {validationNotesCount === 0 && (
                  <div className="rounded-lg border border-green-700/30 bg-green-900/10 px-3 py-2 text-[11px] text-green-200">
                    Nenhuma pendencia encontrada.
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </div>

      <ArtConfigSheet
        item={sheetItem}
        open={Boolean(sheetItem)}
        busy={mutatingItemId === sheetItem?.staticId}
        slotOptions={slotOptions}
        onClose={() => setSheetItemId(null)}
        onFocus={handleFocus}
        onApply={handleApplySlot}
        onClear={handleClearSlot}
      />
    </div>
  );
}
